/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        CAT Translation Projects Store (Task 10.5.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';
import { CatSegment, useCatEditorStore } from './useCatEditorStore';

export type CatFileType = 'docx' | 'pdf' | 'txt' | 'xliff' | 'json';
export type CatProjectStatus = 'in_progress' | 'completed' | 'review' | 'draft';

export interface CatProjectItem {
  id: string;
  title: string;
  fileName: string;
  fileType: CatFileType;
  fileSizeBytes: number;
  sourceLang: string;
  targetLang: string;
  domain: string;
  totalWords: number;
  totalSegments: number;
  completedSegments: number;
  progress: number; // 0 - 100
  translatorName: string;
  status: CatProjectStatus;
  createdAt: string;
  updatedAt: string;
  segments: CatSegment[];
}

export interface CreateProjectParams {
  title: string;
  fileName: string;
  fileType: CatFileType;
  fileSizeBytes?: number;
  sourceLang: string;
  targetLang: string;
  domain: string;
  rawText?: string;
  translatorName?: string;
}

export interface CatProjectsState {
  projects: CatProjectItem[];
  activeProjectId: string;
  searchQuery: string;
  statusFilter: 'all' | CatProjectStatus;
  languageFilter: string;

  // Actions
  setSearchQuery: (query: string) => void;
  setStatusFilter: (status: 'all' | CatProjectStatus) => void;
  setLanguageFilter: (lang: string) => void;
  setActiveProjectId: (id: string) => void;

  createProject: (params: CreateProjectParams) => CatProjectItem;
  deleteProject: (id: string) => void;
  loadProjectToEditor: (id: string) => void;
  syncActiveProjectFromEditor: (segments: CatSegment[]) => void;

  // Selectors
  getFilteredProjects: () => CatProjectItem[];
  getActiveProject: () => CatProjectItem | undefined;
  getProjectStatsSummary: () => {
    totalProjects: number;
    totalWords: number;
    avgProgress: number;
    completedCount: number;
  };
}

/**
 * Splits plain text into structured sentence segments with word counts.
 */
export function segmentizeRawText(text: string): CatSegment[] {
  if (!text || !text.trim()) {
    return [];
  }

  // Split on sentence boundaries (. ? ! followed by whitespace or line breaks)
  const sentences = text
    .split(/(?<=[.?!])\s+|\n{2,}/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  return sentences.map((sentence, idx) => {
    const wordCount = (sentence.match(/[\p{L}\p{N}]+/gu) || []).length;
    return {
      id: `seg_${Date.now()}_${idx + 1}`,
      chunkIndex: idx + 1,
      sequenceOrder: idx,
      sourceText: sentence,
      targetText: '',
      status: 'untranslated',
      matchType: null,
      tmMatchPercent: 0,
      qaWarnings: [],
      wordCount,
      charCount: sentence.length,
    };
  });
}

const SEED_PROJECTS: CatProjectItem[] = [
  {
    id: 'prj_whitepaper_2026',
    title: 'Sophia Platform Cloudflare Architecture Whitepaper',
    fileName: 'sophia-edge-architecture-2026.docx',
    fileType: 'docx',
    fileSizeBytes: 245760,
    sourceLang: 'en',
    targetLang: 'vi',
    domain: 'technology',
    totalWords: 1420,
    totalSegments: 18,
    completedSegments: 12,
    progress: 67,
    translatorName: 'Le Viet Quang',
    status: 'in_progress',
    createdAt: '2026-10-22T08:30:00Z',
    updatedAt: '2026-10-25T07:15:00Z',
    segments: [], // Loaded from initial editor state
  },
  {
    id: 'prj_openapi_spec',
    title: 'Sophia Edge API & Vectorize Reference',
    fileName: 'api-reference-v2.json',
    fileType: 'json',
    fileSizeBytes: 98304,
    sourceLang: 'en',
    targetLang: 'vi',
    domain: 'technology',
    totalWords: 890,
    totalSegments: 12,
    completedSegments: 12,
    progress: 100,
    translatorName: 'Sophia AI Assistant',
    status: 'completed',
    createdAt: '2026-10-20T14:00:00Z',
    updatedAt: '2026-10-24T18:45:00Z',
    segments: [
      {
        id: 'seg_api_01',
        chunkIndex: 1,
        sequenceOrder: 0,
        sourceText: 'The Cloudflare Workers API client provides low latency HTTP invocation.',
        targetText: 'Trình khách API Cloudflare Workers cung cấp khả năng gọi HTTP độ trễ thấp.',
        status: 'approved',
        matchType: 'exact',
        tmMatchPercent: 100,
        qaWarnings: [],
        wordCount: 10,
        charCount: 71,
      },
      {
        id: 'seg_api_02',
        chunkIndex: 2,
        sequenceOrder: 1,
        sourceText: 'Authenticate all requests with bearer token sk_live_{key}.',
        targetText: 'Xác thực toàn bộ yêu cầu với mã khóa bearer sk_live_{key}.',
        status: 'approved',
        matchType: 'manual',
        tmMatchPercent: 0,
        qaWarnings: [],
        wordCount: 8,
        charCount: 57,
      },
    ],
  },
  {
    id: 'prj_privacy_terms',
    title: 'Global Enterprise Terms of Service',
    fileName: 'enterprise-tos-agreements.pdf',
    fileType: 'pdf',
    fileSizeBytes: 524288,
    sourceLang: 'en',
    targetLang: 'vi',
    domain: 'legal',
    totalWords: 2150,
    totalSegments: 24,
    completedSegments: 6,
    progress: 25,
    translatorName: 'Legal Review Team',
    status: 'review',
    createdAt: '2026-10-23T09:00:00Z',
    updatedAt: '2026-10-25T06:00:00Z',
    segments: [],
  },
  {
    id: 'prj_prompt_guide',
    title: 'Prompt Engine Multi-Model Localization Guide',
    fileName: 'prompt-engine-manual.xlf',
    fileType: 'xliff',
    fileSizeBytes: 147456,
    sourceLang: 'en',
    targetLang: 'ja',
    domain: 'marketing',
    totalWords: 620,
    totalSegments: 8,
    completedSegments: 7,
    progress: 88,
    translatorName: 'Kenji Sato',
    status: 'in_progress',
    createdAt: '2026-10-24T11:20:00Z',
    updatedAt: '2026-10-25T08:10:00Z',
    segments: [],
  },
];

export const useCatProjectsStore = create<CatProjectsState>((set, get) => ({
  projects: SEED_PROJECTS,
  activeProjectId: 'prj_whitepaper_2026',
  searchQuery: '',
  statusFilter: 'all',
  languageFilter: 'all',

  setSearchQuery: (query) => set({ searchQuery: query }),
  setStatusFilter: (status) => set({ statusFilter: status }),
  setLanguageFilter: (lang) => set({ languageFilter: lang }),
  setActiveProjectId: (id) => set({ activeProjectId: id }),

  createProject: (params) => {
    const defaultText =
      params.rawText ||
      `Welcome to ${params.title}. This document is translated under the ${params.domain} domain. Cloudflare Workers and D1 database ensure continuous high availability and low latency.`;

    const segments = segmentizeRawText(defaultText);
    const totalWords = segments.reduce((sum, s) => sum + s.wordCount, 0);

    const newProject: CatProjectItem = {
      id: `prj_${Date.now()}`,
      title: params.title.trim() || params.fileName,
      fileName: params.fileName,
      fileType: params.fileType,
      fileSizeBytes: params.fileSizeBytes || 10240,
      sourceLang: params.sourceLang || 'en',
      targetLang: params.targetLang || 'vi',
      domain: params.domain || 'general',
      totalWords: Math.max(totalWords, 120),
      totalSegments: segments.length || 3,
      completedSegments: 0,
      progress: 0,
      translatorName: params.translatorName || 'Le Viet Quang',
      status: 'in_progress',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      segments,
    };

    set((state) => ({
      projects: [newProject, ...state.projects],
      activeProjectId: newProject.id,
    }));

    return newProject;
  },

  deleteProject: (id) => {
    set((state) => {
      const remaining = state.projects.filter((p) => p.id !== id);
      return {
        projects: remaining,
        activeProjectId: state.activeProjectId === id ? (remaining[0]?.id || '') : state.activeProjectId,
      };
    });
  },

  loadProjectToEditor: (id) => {
    const project = get().projects.find((p) => p.id === id);
    if (!project) return;

    set({ activeProjectId: id });

    // Sync metadata to useCatEditorStore
    const editorStore = useCatEditorStore.getState();
    if (project.segments && project.segments.length > 0) {
      useCatEditorStore.setState({
        metadata: {
          documentName: project.fileName,
          sourceLang: project.sourceLang,
          targetLang: project.targetLang,
          domain: project.domain,
        },
        project: {
          id: project.id,
          title: project.title,
          sourceLang: project.sourceLang,
          targetLang: project.targetLang,
          domain: project.domain,
          createdAt: project.createdAt,
        },
        segments: project.segments,
        activeSegmentId: project.segments[0]?.id || null,
      });
    } else {
      // Just update project metadata
      useCatEditorStore.setState({
        metadata: {
          ...editorStore.metadata,
          documentName: project.fileName,
          sourceLang: project.sourceLang,
          targetLang: project.targetLang,
          domain: project.domain,
        },
        project: {
          id: project.id,
          title: project.title,
          sourceLang: project.sourceLang,
          targetLang: project.targetLang,
          domain: project.domain,
          createdAt: project.createdAt,
        },
      });
    }
  },

  syncActiveProjectFromEditor: (segments) => {
    const { activeProjectId, projects } = get();
    if (!activeProjectId) return;

    const completed = segments.filter((s) => s.status === 'approved' || (s.targetText && s.targetText.trim().length > 0)).length;
    const progress = segments.length > 0 ? Math.round((completed / segments.length) * 100) : 0;
    const status: CatProjectStatus = progress === 100 ? 'completed' : progress > 0 ? 'in_progress' : 'draft';

    set({
      projects: projects.map((p) => {
        if (p.id !== activeProjectId) return p;
        return {
          ...p,
          segments,
          totalSegments: segments.length,
          completedSegments: completed,
          progress,
          status: p.status === 'review' && progress < 100 ? 'review' : status,
          updatedAt: new Date().toISOString(),
        };
      }),
    });
  },

  getFilteredProjects: () => {
    const { projects, searchQuery, statusFilter, languageFilter } = get();
    const query = searchQuery.trim().toLowerCase();

    return projects.filter((p) => {
      // Status filter
      if (statusFilter !== 'all' && p.status !== statusFilter) {
        return false;
      }
      // Language filter
      if (languageFilter !== 'all' && p.targetLang !== languageFilter) {
        return false;
      }
      // Search query
      if (query) {
        const matchesTitle = p.title.toLowerCase().includes(query);
        const matchesFile = p.fileName.toLowerCase().includes(query);
        const matchesTranslator = p.translatorName.toLowerCase().includes(query);
        return matchesTitle || matchesFile || matchesTranslator;
      }
      return true;
    });
  },

  getActiveProject: () => {
    const { projects, activeProjectId } = get();
    return projects.find((p) => p.id === activeProjectId) || projects[0];
  },

  getProjectStatsSummary: () => {
    const { projects } = get();
    const totalProjects = projects.length;
    const totalWords = projects.reduce((sum, p) => sum + p.totalWords, 0);
    const completedCount = projects.filter((p) => p.status === 'completed').length;
    const totalProgress = projects.reduce((sum, p) => sum + p.progress, 0);
    const avgProgress = totalProjects > 0 ? Math.round(totalProgress / totalProjects) : 0;

    return {
      totalProjects,
      totalWords,
      avgProgress,
      completedCount,
    };
  },
}));
